/**
 * 🖼️ VERIFICATION DOCUMENT VIEWER
 * Component for displaying and managing verification document images
 * Integrates with the new admin verification endpoints
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
  Tooltip,
  Zoom,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateRightIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PhotoLibrary
} from '@mui/icons-material';
import { verificationService, AdminVerificationRequest } from '@/services/verificationService';
import { DocumentThumbnailGallery, type DocumentInfo } from '@/components/documents/DocumentThumbnailGallery';
import { DocumentQualityAnalyzer, type QualityMetrics } from '@/components/documents/DocumentQualityAnalyzer';
import { PasswordReAuthDialog, PIIAccessToken } from '@/components/security/PasswordReAuthDialog';
import { piiTokenManager } from '@/utils/piiTokenManager';
import { logger } from '@/utils/logger';

const log = logger.module('VerificationDocumentViewer');

interface VerificationDocumentViewerProps {
  open: boolean;
  onClose: () => void;
  verificationRequest: AdminVerificationRequest;
  onApprove?: (verificationId: string, notes?: string) => Promise<void>;
  onReject?: (verificationId: string, notes?: string, rejectedFields?: string[]) => Promise<void>;
}

interface ImageViewState {
  zoom: number;
  rotation: number;
  currentImageIndex: number;
}

const VerificationDocumentViewer: React.FC<VerificationDocumentViewerProps> = ({
  open,
  onClose,
  verificationRequest,
  onApprove,
  onReject
}) => {
  const [imageViewState, setImageViewState] = useState<ImageViewState>({
    zoom: 1,
    rotation: 0,
    currentImageIndex: 0
  });
  const [loadingImages, setLoadingImages] = useState<string[]>([]);
  const [imageErrors, setImageErrors] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [rejectedFields, setRejectedFields] = useState<string[]>([]);
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [showThumbnailMode, setShowThumbnailMode] = useState(false);

  // PII Security - Password re-authentication
  const [showPasswordDialog, setShowPasswordDialog] = useState(true);
  const [piiToken, setPiiToken] = useState<string | null>(null);
  const [secureBlobUrls, setSecureBlobUrls] = useState<{document?: string; selfie?: string}>({});
  const [fetchingSecureImages, setFetchingSecureImages] = useState(false);

  const rejectionReasons = [
    'Document is unclear or blurry',
    'Document appears to be fake or altered',
    'Document has expired',
    'Selfie does not match document photo',
    'Document type not accepted',
    'Information is incomplete or illegible',
    'Suspicious activity detected',
    'Other (specify below)'
  ];
  
  const fieldOptions = [
    'Document Photo',
    'Selfie Photo',
    'Personal Information',
    'Document Number',
    'Expiry Date',
    'Issue Date'
  ];

  // PII Security Handlers
  const handlePasswordVerified = async (token: PIIAccessToken) => {

    // Store token
    piiTokenManager.setToken(token);
    setPiiToken(token.token);
    setShowPasswordDialog(false);

    // Fetch images with PII token
    await fetchSecureImages(token.token);
  };

  const fetchSecureImages = async (token: string) => {
    setFetchingSecureImages(true);
    const newBlobUrls: {document?: string; selfie?: string} = {};

    try {
      // Fetch document image with PII token
      if (verificationRequest.documentImageUrl) {
        const result = await verificationService.fetchVerificationImageWithPIIToken(
          verificationRequest.documentImageUrl,
          token
        );

        if (result.success && result.blobUrl) {
          newBlobUrls.document = result.blobUrl;
        } else if (result.error) {
          console.error('❌ Failed to fetch document image:', result.error);
          if (result.error.includes('expired') || result.error.includes('invalid')) {
            // Token expired, show password dialog again
            setShowPasswordDialog(true);
            setPiiToken(null);
            return;
          }
        }
      }

      // Fetch selfie image with PII token
      if (verificationRequest.selfieImageUrl) {
        const result = await verificationService.fetchVerificationImageWithPIIToken(
          verificationRequest.selfieImageUrl,
          token
        );

        if (result.success && result.blobUrl) {
          newBlobUrls.selfie = result.blobUrl;
        } else if (result.error) {
          console.error('❌ Failed to fetch selfie image:', result.error);
          if (result.error.includes('expired') || result.error.includes('invalid')) {
            // Token expired, show password dialog again
            setShowPasswordDialog(true);
            setPiiToken(null);
            return;
          }
        }
      }

      setSecureBlobUrls(newBlobUrls);
    } catch (error) {
      console.error('❌ Error fetching secure images:', error);
    } finally {
      setFetchingSecureImages(false);
    }
  };

  const handleRejectClick = () => {
    setShowRejectDialog(true);
  };

  const handleRejectConfirm = async () => {
    const finalReason = rejectionReason === 'Other (specify below)' ? customReason : rejectionReason;
    
    if (!finalReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setIsProcessing(true);
    try {
      await onReject?.(verificationRequest.verificationId, finalReason, rejectedFields);
      setShowRejectDialog(false);
      setRejectionReason('');
      setCustomReason('');
      setRejectedFields([]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectCancel = () => {
    setShowRejectDialog(false);
    setRejectionReason('');
    setCustomReason('');
    setRejectedFields([]);
  };

  const handleImageLoad = (imagePath: string) => {
    setLoadingImages(prev => prev.filter(path => path !== imagePath));
  };

  const handleImageError = (imagePath: string) => {
    console.error('🚨 Image failed to load:', imagePath);
    setLoadingImages(prev => prev.filter(path => path !== imagePath));
    setImageErrors(prev => [...prev, imagePath]);
  };

  const handleImageClick = (index: number) => {
    setImageViewState(prev => ({
      ...prev,
      currentImageIndex: index,
      zoom: 1,
      rotation: 0
    }));
  };

  const handleZoomIn = () => {
    setImageViewState(prev => ({
      ...prev,
      zoom: Math.min(prev.zoom + 0.25, 3)
    }));
  };

  const handleZoomOut = () => {
    setImageViewState(prev => ({
      ...prev,
      zoom: Math.max(prev.zoom - 0.25, 0.5)
    }));
  };

  const handleRotate = () => {
    setImageViewState(prev => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360
    }));
  };

  const handleDownloadImage = async (imagePath: string) => {
    setIsDownloading(true);
    try {
      const result = await verificationService.downloadVerificationDocument(imagePath);
      if (result.success && result.blob) {
        // Create download link
        const url = window.URL.createObjectURL(result.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `verification_${verificationRequest.verificationId}_${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Failed to download image:', result.error);
      }
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getDocumentTypeColor = (docType: string) => {
    const colors = {
      passport: 'primary',
      national_id: 'secondary',
      driver_license: 'success',
      selfie: 'info'
    } as const;
    return colors[docType as keyof typeof colors] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending_review: 'warning',
      verified: 'success',
      rejected: 'error'
    } as const;
    return colors[status as keyof typeof colors] || 'default';
  };

  // Use secure blob URLs if PII token is verified, otherwise show placeholder
  const images = [
    secureBlobUrls.document || null,
    secureBlobUrls.selfie || null
  ];
  const currentImage = images[imageViewState.currentImageIndex];
  const currentImageUrl = currentImage || '';

  // Convert verification request to document format for thumbnail gallery
  // Use secure blob URLs once they're loaded
  React.useEffect(() => {
    if (!verificationRequest) return;

    const docs: DocumentInfo[] = [];

    // Add document image if available
    if (verificationRequest.documentImageUrl) {
      docs.push({
        id: 'document',
        type: 'national_id',
        url: secureBlobUrls.document || '', // Use secure blob URL
        fileName: `${verificationRequest.documentType}_document.jpg`,
        uploadedAt: verificationRequest.uploadedAt,
        status: verificationRequest.status === 'verified' ? 'approved' :
                verificationRequest.status === 'rejected' ? 'rejected' : 'pending',
        quality: 'good' // Could be analyzed from actual document
      });
    }

    // Add selfie image if available
    if (verificationRequest.selfieImageUrl) {
      docs.push({
        id: 'selfie',
        type: 'selfie',
        url: secureBlobUrls.selfie || '', // Use secure blob URL
        fileName: 'selfie_verification.jpg',
        uploadedAt: verificationRequest.uploadedAt,
        status: verificationRequest.status === 'verified' ? 'approved' :
                verificationRequest.status === 'rejected' ? 'rejected' : 'pending',
        quality: 'good'
      });
    }

    setDocuments(docs);
  }, [verificationRequest, secureBlobUrls]);

  // Debug logging to track image URLs (only on initial open)
  React.useEffect(() => {
    if (open && verificationRequest) {
      // Image URL tracking here
    }
  }, [open, verificationRequest.verificationId]); // Only log when dialog opens or verification changes

  // Initialize loading states when dialog opens
  React.useEffect(() => {
    if (open && verificationRequest) {
      // Clear loading states when dialog opens
      setLoadingImages([]);
      setImageErrors([]);

      // Check if we have a valid PII token
      const existingToken = piiTokenManager.getToken();
      if (existingToken) {
        setPiiToken(existingToken);
        setShowPasswordDialog(false);
        fetchSecureImages(existingToken);
      } else {
        // No valid token, show password dialog
        setShowPasswordDialog(true);
      }
    }
  }, [open, verificationRequest.verificationId]);

  // Cleanup blob URLs on unmount or when dialog closes
  React.useEffect(() => {
    return () => {
      // Revoke blob URLs to prevent memory leaks
      if (secureBlobUrls.document) {
        URL.revokeObjectURL(secureBlobUrls.document);
      }
      if (secureBlobUrls.selfie) {
        URL.revokeObjectURL(secureBlobUrls.selfie);
      }
    };
  }, [secureBlobUrls]);

  return (
    <React.Fragment>
      <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" component="div">
              Verification Request Details
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip 
                label={verificationRequest.documentType.replace('_', ' ').toUpperCase()}
                color={getDocumentTypeColor(verificationRequest.documentType)}
                size="small"
              />
              <Chip 
                label={verificationRequest.status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(verificationRequest.status)}
                size="small"
              />
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              variant={showThumbnailMode ? "contained" : "outlined"}
              onClick={() => setShowThumbnailMode(!showThumbnailMode)}
              startIcon={<PhotoLibrary />}
            >
              {showThumbnailMode ? 'Detail View' : 'Thumbnail View'}
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {showThumbnailMode ? (
          // Thumbnail Gallery Mode
          <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: 'fit-content' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Information
                  </Typography>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Name</Typography>
                    <Typography variant="body1">{verificationRequest.userInfo.firstName} {verificationRequest.userInfo.lastName}</Typography>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Email</Typography>
                    <Typography variant="body1">{verificationRequest.userInfo.email}</Typography>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Submitted</Typography>
                    <Typography variant="body1">
                      {new Date(verificationRequest.uploadedAt).toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              
              {/* Document Quality Analysis */}
              <Box mt={2}>
                {documents.map(doc => (
                  <Box key={doc.id} mb={2}>
                    <DocumentQualityAnalyzer
                      imageUrl={doc.url}
                      documentType={doc.type}
                      showDetails={true}
                      compact={false}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <DocumentThumbnailGallery
                documents={documents}
                title="Verification Documents"
                showQualityIndicators={true}
                showBatchActions={false}
                allowFullscreen={true}
                onDocumentApprove={(docId, notes) => {
                  // Handle individual document approval
                }}
                onDocumentReject={(docId, reason) => {
                  // Handle individual document rejection
                }}
                onDocumentFlag={(docId, reason) => {
                  // Handle document flagging
                }}
              />
            </Grid>
          </Grid>
        ) : (
          // Original Detail Mode
          <Grid container spacing={2} sx={{ height: '100%' }}>
          {/* User Information Panel */}
          <Grid item xs={12} md={4} sx={{ maxHeight: '100%', overflowY: 'auto' }}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  User Information
                </Typography>
                
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">Name</Typography>
                  <Typography variant="body1">{verificationRequest.userInfo.firstName} {verificationRequest.userInfo.lastName}</Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{verificationRequest.userInfo.email}</Typography>
                </Box>

                {verificationRequest.userInfo.phone && (
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Phone</Typography>
                    <Typography variant="body1">{verificationRequest.userInfo.phone}</Typography>
                  </Box>
                )}

                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">Submitted</Typography>
                  <Typography variant="body1">
                    {new Date(verificationRequest.uploadedAt).toLocaleString()}
                  </Typography>
                </Box>

                {verificationRequest.documentData && (
                  <Box mb={2}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="body2" color="textSecondary">
                        Document Data
                      </Typography>
                      <Tooltip title={showSensitiveData ? "Hide sensitive data" : "Show sensitive data"}>
                        <IconButton 
                          size="small" 
                          onClick={() => setShowSensitiveData(!showSensitiveData)}
                        >
                          {showSensitiveData ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    {showSensitiveData && (
                      <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                        <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(verificationRequest.documentData, null, 2)}
                        </pre>
                      </Box>
                    )}
                  </Box>
                )}

                {verificationRequest.notes && (
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Admin Notes</Typography>
                    <Typography variant="body1">{verificationRequest.notes}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Image Thumbnails */}
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                📎 Documents (click to switch)
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      border: imageViewState.currentImageIndex === 0 ? 2 : 0,
                      borderColor: 'primary.main',
                      position: 'relative'
                    }}
                    onClick={() => handleImageClick(0)}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: 'absolute', 
                        top: 4, 
                        left: 4, 
                        bgcolor: 'rgba(0,0,0,0.7)', 
                        color: 'white', 
                        px: 1, 
                        borderRadius: 1,
                        fontSize: '10px'
                      }}
                    >
                      Document
                    </Typography>
                    {verificationRequest.documentImageUrl ? (
                      <CardMedia
                        component="img"
                        height="80"
                        image={images[0]}
                        alt="Document Image"
                        onLoad={() => handleImageLoad(images[0] || '')}
                        onError={() => handleImageError(images[0] || '')}
                        sx={{
                          objectFit: 'cover',
                          filter: imageErrors.includes(images[0] || '') ? 'grayscale(100%)' : 'none',
                          display: loadingImages.includes(images[0] || '') ? 'none' : 'block'
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          height: 80, 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          bgcolor: 'grey.100'
                        }}
                      >
                        <Box sx={{ fontSize: 16, mb: 0.5 }}>📄</Box>
                        <Typography variant="caption" color="textSecondary" textAlign="center">
                          Processing...
                        </Typography>
                      </Box>
                    )}
                    {loadingImages.includes(images[0] || '') && (
                      <Box 
                        position="absolute" 
                        top="50%" 
                        left="50%" 
                        sx={{ transform: 'translate(-50%, -50%)' }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      border: imageViewState.currentImageIndex === 1 ? 2 : 0,
                      borderColor: 'primary.main',
                      position: 'relative'
                    }}
                    onClick={() => handleImageClick(1)}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: 'absolute', 
                        top: 4, 
                        left: 4, 
                        bgcolor: 'rgba(0,0,0,0.7)', 
                        color: 'white', 
                        px: 1, 
                        borderRadius: 1,
                        fontSize: '10px'
                      }}
                    >
                      Selfie
                    </Typography>
                    {verificationRequest.selfieImageUrl ? (
                      <CardMedia
                        component="img"
                        height="80"
                        image={images[1]}
                        alt="Selfie Image"
                        onLoad={() => handleImageLoad(images[1] || '')}
                        onError={() => handleImageError(images[1] || '')}
                        sx={{
                          objectFit: 'cover',
                          filter: imageErrors.includes(images[1] || '') ? 'grayscale(100%)' : 'none',
                          display: loadingImages.includes(images[1] || '') ? 'none' : 'block'
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          height: 80, 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          bgcolor: 'grey.100'
                        }}
                      >
                        <Box sx={{ fontSize: 16, mb: 0.5 }}>📤</Box>
                        <Typography variant="caption" color="textSecondary" textAlign="center">
                          Processing...
                        </Typography>
                      </Box>
                    )}
                    {loadingImages.includes(images[1] || '') && (
                      <Box 
                        position="absolute" 
                        top="50%" 
                        left="50%" 
                        sx={{ transform: 'translate(-50%, -50%)' }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Main Image Viewer */}
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%', position: 'relative' }}>
              {/* Image Controls */}
              <Box 
                position="absolute" 
                top={8} 
                right={8} 
                zIndex={1}
                display="flex"
                gap={1}
              >
                <Tooltip title="Zoom In">
                  <IconButton 
                    onClick={handleZoomIn}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                  >
                    <ZoomInIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Zoom Out">
                  <IconButton 
                    onClick={handleZoomOut}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                  >
                    <ZoomOutIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Rotate">
                  <IconButton 
                    onClick={handleRotate}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                  >
                    <RotateRightIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Download">
                  <IconButton 
                    onClick={() => currentImage && handleDownloadImage(currentImage)}
                    size="small"
                    disabled={isDownloading}
                    sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                  >
                    {isDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Main Image Display */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
                overflow="auto"
                p={2}
              >
                {fetchingSecureImages ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="textSecondary">
                      Securely loading documents...
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                      Access logged in audit trail
                    </Typography>
                  </Box>
                ) : currentImage ? (
                  imageErrors.includes(currentImage) ? (
                    <Alert severity="error">
                      Failed to load image: {currentImage.substring(0, 100)}...
                    </Alert>
                  ) : (
                    <Box position="relative" width="100%" height="100%">
                      {loadingImages.includes(currentImage) && (
                        <Box 
                          position="absolute" 
                          top="50%" 
                          left="50%" 
                          sx={{ transform: 'translate(-50%, -50%)' }}
                        >
                          <CircularProgress size={40} />
                          <Typography variant="body2" color="textSecondary" textAlign="center" mt={1}>
                            Loading image...
                          </Typography>
                        </Box>
                      )}
                      <Zoom in={!loadingImages.includes(currentImage)}>
                        <img
                          src={currentImageUrl}
                          alt={`Verification document ${imageViewState.currentImageIndex + 1}`}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            width: 'auto',
                            height: 'auto',
                            transform: `scale(${imageViewState.zoom}) rotate(${imageViewState.rotation}deg)`,
                            transition: 'transform 0.3s ease',
                            objectFit: 'contain',
                            display: loadingImages.includes(currentImage) ? 'none' : 'block',
                            margin: 'auto',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0
                          }}
                          onLoad={() => handleImageLoad(currentImage)}
                          onError={() => handleImageError(currentImage)}
                        />
                      </Zoom>
                    </Box>
                  )
                ) : (
                  <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center"
                    height="100%"
                    sx={{ bgcolor: 'grey.50', borderRadius: 1 }}
                  >
                    <Box sx={{ fontSize: 48, color: 'grey.400', mb: 2 }}>
                      📄
                    </Box>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {imageViewState.currentImageIndex === 0 ? 'Document Image' : 'Selfie Image'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" textAlign="center">
                      Backend is processing large base64 image data.
                      <br />
                      Image will appear once processing is complete.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
        )}
      </DialogContent>

      {/* Action Buttons */}
      <DialogActions>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
          
          {(verificationRequest.status === 'pending_review' || verificationRequest.status === 'pending_manual_review') && (
            <Box display="flex" gap={1}>
              <Button
                onClick={handleRejectClick}
                color="error"
                variant="contained"
                disabled={isProcessing}
              >
                Reject
              </Button>
              <Button
                onClick={async () => {
                  log.info('Approve button clicked', { verificationId: verificationRequest.verificationId });
                  setIsProcessing(true);
                  try {
                    await onApprove?.(verificationRequest.verificationId);
                    log.info('Approval completed successfully', { verificationId: verificationRequest.verificationId });
                  } catch (error) {
                    log.error('Error during approval', { verificationId: verificationRequest.verificationId, error });
                    alert(`Failed to approve verification: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                color="success"
                variant="contained"
                disabled={isProcessing}
              >
                {isProcessing ? 'Approving...' : 'Approve'}
              </Button>
            </Box>
          )}
        </Box>
      </DialogActions>
      </Dialog>

      {/* Rejection Reason Dialog */}
      <Dialog
        open={showRejectDialog}
        onClose={handleRejectCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Reject Verification Request
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please provide a reason for rejecting this verification. The user will receive an email notification with your feedback.
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Rejection Reason</InputLabel>
            <Select
              value={rejectionReason}
              label="Rejection Reason"
              onChange={(e) => setRejectionReason(e.target.value)}
            >
              {rejectionReasons.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Fields that need correction */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select fields that need correction:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {fieldOptions.map((field) => {
                const isSelected = rejectedFields.includes(field);
                return (
                  <Chip
                    key={field}
                    label={field}
                    onClick={() => {
                      setRejectedFields(prev =>
                        prev.includes(field)
                          ? prev.filter(f => f !== field)
                          : [...prev, field]
                      );
                    }}
                    color={isSelected ? "error" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      ...(isSelected && {
                        backgroundColor: 'error.main',
                        color: 'error.contrastText',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: 'error.dark',
                        }
                      }),
                      ...(!isSelected && {
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderColor: 'error.main',
                        }
                      })
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {rejectionReason === 'Other (specify below)' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Custom Reason"
              placeholder="Please specify the reason for rejection..."
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              The user will receive an email with the rejection reason and instructions on how to resubmit their verification.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleRejectCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={isProcessing || !rejectionReason}
          >
            {isProcessing ? 'Rejecting...' : 'Reject Verification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PII Security - Password Re-Authentication Dialog */}
      <PasswordReAuthDialog
        open={showPasswordDialog && open}
        onClose={() => {
          // User cancelled password verification, close main dialog too
          setShowPasswordDialog(false);
          onClose();
        }}
        onSuccess={handlePasswordVerified}
        resourceType="photos"
        resourceId={verificationRequest.verificationId}
        title="Verify Password to View Documents"
        description="You must verify your password before viewing user documents. This action will be logged in the audit trail for compliance."
      />
    </React.Fragment>
  );
};

export default VerificationDocumentViewer;